import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fihristBul, trDuz, type FihristMadde } from '../kilavuz/fihrist';
import { useAuth } from '../lib/auth';
import { fihristKatmaniBagla, kutuAl, type BalonIstegi } from './Ipucu';

/**
 * FİHRİST KATMANI — kabukta BİR KEZ durur ve iki şeyi çizer:
 *
 *  1. «?» balonu: `Ipucu` bileşenleri "aç" der, balon burada sabit konumda
 *     açılır; ekranın kenarından taşmaz, tablo başlığının altında kalmaz.
 *  2. Sağ tuş menüsü: `data-fihrist="<id>"` taşıyan bir öğeye sağ tıklanınca
 *     (dokunmatikte uzun basınca) "Bu nedir? Nasıl hesaplanır?" menüsü.
 *
 * Tarayıcının kendi sağ tuş menüsü YALNIZ işaretli öğelerde değişir. İşaretli
 * öğede bile şu durumlarda tarayıcınınki açılır: Shift basılıyken, bir metin
 * seçiliyken, yazı kutusunda ve işaretli alanın içindeki bir bağlantıda
 * ("yeni sekmede aç" kaybolmasın diye).
 */
type Menu = { id: string; x: number; y: number; hedef: Element };

/**
 * Kartın kendi notu fihristteki açıklamayı yineliyorsa balonda gösterilmez;
 * yalnız o ekrana özgü bir şey söylüyorsa ("bu dönemin faturalarından…") gösterilir.
 */
function ozgunNot(not: string | undefined, m: FihristMadde): string | null {
  if (!not) return null;
  const bilinen = new Set(trDuz(`${m.kisa} ${m.nasilHesaplanir ?? ''}`).split(' '));
  const kelimeler = trDuz(not).split(' ').filter((k) => k.length > 3);
  if (kelimeler.length === 0) return null;
  const ortak = kelimeler.filter((k) => bilinen.has(k)).length;
  return ortak / kelimeler.length >= 0.7 ? null : not;
}
const KENAR = 8;
const UZUN_BASIS_MS = 550;

export default function FihristKatmani() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { can } = useAuth();
  const [balon, setBalon] = useState<BalonIstegi | null>(null);
  const [menu, setMenu] = useState<Menu | null>(null);
  const [yer, setYer] = useState<{ sol: number; ust: number } | null>(null);
  const balonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const zaman = useRef<number | null>(null);
  const balonSimdi = useRef<BalonIstegi | null>(null);
  balonSimdi.current = balon;
  /** Esc ile kapatınca odak «?»e döner; o odak balonu yeniden açmasın. */
  const sus = useRef<{ el: HTMLElement | null; an: number }>({ el: null, an: 0 });

  const sureyiSil = () => { if (zaman.current != null) { window.clearTimeout(zaman.current); zaman.current = null; } };
  const kapat = useCallback((odakDon = false) => {
    sureyiSil();
    const kaynak = balonSimdi.current?.kaynak ?? null;
    if (odakDon && kaynak) { sus.current = { el: kaynak, an: Date.now() }; kaynak.focus?.(); }
    setBalon(null); setYer(null);
  }, []);

  /** Rolün görmediği madde için ne balon ne menü açılır. */
  const gorunur = useCallback((id: string | null | undefined) => {
    const m = fihristBul(id);
    return m && (!m.cap || can(m.cap)) ? m : undefined;
  }, [can]);

  /* ── «?» düğmelerinden gelen istekler ─────────────────────────────────── */
  useEffect(() => {
    fihristKatmaniBagla((o) => {
      if (o.tur === 'bekle') { sureyiSil(); return; }
      if (o.tur === 'ac') {
        sureyiSil();
        if (!gorunur(o.istek.id)) return;
        if (!o.istek.sabit && sus.current.el === o.istek.kaynak && Date.now() - sus.current.an < 300) return;
        // Tıklamayla sabitlenmiş balonu, aynı «?» üstünde gezinmek çözmesin.
        const simdi = balonSimdi.current;
        if (simdi?.sabit && !o.istek.sabit && simdi.kaynak === o.istek.kaynak) return;
        // Aynı «?» ikinci kez tıklanırsa kapanır (dokunmatikte aç-kapa).
        if (simdi?.sabit && o.istek.sabit && simdi.kaynak === o.istek.kaynak) { kapat(); return; }
        setMenu(null); setYer(null); setBalon(o.istek);
        return;
      }
      if (o.hemen) { kapat(true); return; }
      if (balonSimdi.current?.sabit) return;
      sureyiSil();
      zaman.current = window.setTimeout(() => { setBalon(null); setYer(null); }, 220);
    });
    return () => { fihristKatmaniBagla(null); sureyiSil(); };
  }, [kapat, gorunur]);

  // Sayfa değişince açık ne varsa kapanır.
  useEffect(() => { kapat(); setMenu(null); }, [pathname, kapat]);

  /* ── Balonu yerleştir: önce üstüne, sığmazsa altına; yanlardan taşmaz ─── */
  useLayoutEffect(() => {
    const el = balonRef.current;
    if (!balon || !el) return;
    const w = el.offsetWidth; const h = el.offsetHeight;
    const vw = window.innerWidth; const vh = window.innerHeight;
    const orta = (balon.kutu.sol + balon.kutu.sag) / 2;
    const sol = Math.max(KENAR, Math.min(orta - w / 2, vw - w - KENAR));
    const ustte = balon.kutu.ust - h - 8;
    const altta = balon.kutu.alt + 8;
    const ust = ustte >= KENAR ? ustte : altta + h <= vh - KENAR ? altta : Math.max(KENAR, vh - h - KENAR);
    setYer({ sol, ust });
    if (balon.odak) el.querySelector<HTMLAnchorElement>('a')?.focus({ preventScroll: true });
  }, [balon]);

  /* ── Menüyü ekranın içine al ve ilk seçeneğe odaklan ──────────────────── */
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!menu || !el) return;
    const sol = Math.max(KENAR, Math.min(menu.x, window.innerWidth - el.offsetWidth - KENAR));
    const ust = Math.max(KENAR, Math.min(menu.y, window.innerHeight - el.offsetHeight - KENAR));
    el.style.left = `${sol}px`; el.style.top = `${ust}px`; el.style.visibility = 'visible';
    el.querySelector<HTMLButtonElement>('button')?.focus();
  }, [menu]);

  /* ── Dışarı tıklama, Esc, kaydırma, boyut değişimi ────────────────────── */
  useEffect(() => {
    if (!balon && !menu) return;
    const disari = (e: Event) => {
      const t = e.target as Node | null;
      if (t && (balonRef.current?.contains(t) || menuRef.current?.contains(t))) return;
      if (t instanceof Element && t.closest('[data-fihrist-dugme]')) return;
      kapat(); setMenu(null);
    };
    const tus = (e: KeyboardEvent) => { if (e.key === 'Escape') { kapat(true); setMenu(null); } };
    const kay = (e: Event) => {
      // Balonun kendi içi kaydırılıyorsa kapanmasın.
      if (e.target instanceof Node && balonRef.current?.contains(e.target)) return;
      kapat(); setMenu(null);
    };
    document.addEventListener('pointerdown', disari, true);
    document.addEventListener('keydown', tus);
    window.addEventListener('scroll', kay, true);
    window.addEventListener('resize', kay);
    return () => {
      document.removeEventListener('pointerdown', disari, true);
      document.removeEventListener('keydown', tus);
      window.removeEventListener('scroll', kay, true);
      window.removeEventListener('resize', kay);
    };
  }, [balon, menu, kapat]);

  /* ── Sağ tuş ve uzun basış ────────────────────────────────────────────── */
  useEffect(() => {
    /** İşaretli öğeyi bul; tarayıcı menüsünün açılması gereken durumlarda null. */
    const hedefBul = (t: EventTarget | null): { el: Element; id: string } | null => {
      if (!(t instanceof Element)) return null;
      const el = t.closest('[data-fihrist]');
      const id = el?.getAttribute('data-fihrist');
      if (!el || !id || !gorunur(id)) return null;
      if (t.closest('input, textarea, select, [contenteditable="true"]')) return null;
      const a = t.closest('a[href]');
      if (a && a !== el && el.contains(a)) return null;
      return { el, id };
    };
    const menuAc = (h: { el: Element; id: string }, x: number, y: number) => {
      kapat(); setMenu({ id: h.id, x, y, hedef: h.el });
    };

    const sagTus = (e: MouseEvent) => {
      if (e.shiftKey) return;
      const secim = window.getSelection();
      if (secim && !secim.isCollapsed && secim.toString().trim()) return;
      const h = hedefBul(e.target);
      if (!h) return;
      e.preventDefault();
      menuAc(h, e.clientX, e.clientY);
    };

    // Dokunmatik: basılı tutunca aynı menü. (iOS'ta "contextmenu" olayı gelmez.)
    let basis: number | null = null;
    let bas = { x: 0, y: 0 };
    let tiklamayiYut = false;
    const basisiBirak = () => { if (basis != null) { window.clearTimeout(basis); basis = null; } };
    const dokun = (e: TouchEvent) => {
      basisiBirak();
      if (e.touches.length !== 1) return;
      const h = hedefBul(e.target);
      if (!h) return;
      bas = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      basis = window.setTimeout(() => {
        basis = null;
        tiklamayiYut = true;
        window.setTimeout(() => { tiklamayiYut = false; }, 900);
        menuAc(h, bas.x, bas.y);
      }, UZUN_BASIS_MS);
    };
    const kaydi = (e: TouchEvent) => {
      const d = e.touches[0];
      if (!d || Math.abs(d.clientX - bas.x) > 10 || Math.abs(d.clientY - bas.y) > 10) basisiBirak();
    };
    // Uzun basıştan sonra parmak kalkınca oluşan tıklama kartı açmasın.
    const tikla = (e: MouseEvent) => {
      if (!tiklamayiYut) return;
      tiklamayiYut = false;
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      e.preventDefault(); e.stopPropagation();
    };

    document.addEventListener('contextmenu', sagTus);
    document.addEventListener('touchstart', dokun, { passive: true });
    document.addEventListener('touchmove', kaydi, { passive: true });
    document.addEventListener('touchend', basisiBirak);
    document.addEventListener('touchcancel', basisiBirak);
    document.addEventListener('click', tikla, true);
    return () => {
      basisiBirak();
      document.removeEventListener('contextmenu', sagTus);
      document.removeEventListener('touchstart', dokun);
      document.removeEventListener('touchmove', kaydi);
      document.removeEventListener('touchend', basisiBirak);
      document.removeEventListener('touchcancel', basisiBirak);
      document.removeEventListener('click', tikla, true);
    };
  }, [kapat, gorunur]);

  const madde = balon ? fihristBul(balon.id) : undefined;
  const menuMadde = menu ? fihristBul(menu.id) : undefined;
  const fihristeGit = (id: string) => { kapat(); setMenu(null); navigate(`/fihrist#${id}`); };

  return (
    <>
      {balon && madde && (
        <div ref={balonRef} className="ccd-fbalon" role="dialog" aria-label={`${madde.baslik} — açıklama`}
             style={yer ? { left: yer.sol, top: yer.ust } : { left: 0, top: 0, visibility: 'hidden' }}
             onMouseEnter={sureyiSil}
             onMouseLeave={() => { if (!balon.sabit) { sureyiSil(); zaman.current = window.setTimeout(() => { setBalon(null); setYer(null); }, 220); } }}>
          <div className="ccd-fbalon-baslik">{madde.baslik}</div>
          <div className="ccd-fbalon-kisa">{madde.kisa}</div>
          {madde.nasilHesaplanir && (
            <>
              <div className="ccd-fbalon-etiket">Nasıl hesaplanır</div>
              <div className="ccd-fbalon-hesap">{madde.nasilHesaplanir}</div>
            </>
          )}
          {ozgunNot(balon.not, madde) && (
            <>
              <div className="ccd-fbalon-etiket">Bu ekranda</div>
              <div className="ccd-fbalon-hesap">{balon.not}</div>
            </>
          )}
          <Link className="ccd-fbalon-bag" to={`/fihrist#${madde.id}`} onClick={() => kapat()}>Fihristte aç →</Link>
        </div>
      )}

      {menu && menuMadde && (
        <div ref={menuRef} className="ccd-fmenu" role="menu" aria-label={`${menuMadde.baslik} — yardım`}
             style={{ left: menu.x, top: menu.y, visibility: 'hidden' }}
             onKeyDown={(e) => {
               if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
               e.preventDefault();
               const dugmeler = [...(menuRef.current?.querySelectorAll('button') ?? [])];
               const i = dugmeler.indexOf(document.activeElement as HTMLButtonElement);
               dugmeler[(i + (e.key === 'ArrowDown' ? 1 : dugmeler.length - 1)) % dugmeler.length]?.focus();
             }}>
          <div className="ccd-fmenu-baslik">{menuMadde.baslik}</div>
          <button type="button" role="menuitem" onClick={() => {
            const m = menu;
            setMenu(null);
            // Balon, tıklanan yerin yanında açılır (büyük kartta ortada kaybolmasın).
            const k = kutuAl(m.hedef);
            const dar = k.sag - k.sol < 260 && k.alt - k.ust < 120;
            setYer(null);
            setBalon({ id: m.id, kutu: dar ? k : { sol: m.x, sag: m.x, ust: m.y - 6, alt: m.y + 6 }, sabit: true });
          }}>Bu nedir? Nasıl hesaplanır?</button>
          <button type="button" role="menuitem" onClick={() => fihristeGit(menu.id)}>Fihristte aç</button>
        </div>
      )}
    </>
  );
}
